import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = t('auth.register.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth.register.errors.emailInvalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('auth.register.errors.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('auth.register.errors.passwordTooShort');
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.register.errors.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.register.errors.passwordsNotMatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const result = await register(formData.email, formData.password);
      
      if (result.success) {
        // После успешной регистрации перенаправляем на страницу входа
        navigate('/login', { 
          state: { message: t('auth.register.successMessage') }
        });
      } else {
        // Используем errorKey для получения локализованного сообщения
        const errorMessage = result.errorKey ? t(result.errorKey) : t('auth.register.errors.unknownError');
        setErrors({ 
          submit: errorMessage
        });
      }
    } catch (error) {
      setErrors({ 
        submit: t('auth.register.errors.unknownError')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        {/* Логотип и заголовок */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-600 mb-2">{t('app.name')}</h1>
          <p className="text-gray-600">{t('app.title')}</p>
        </div>

        {/* Форма регистрации */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('auth.register.title')}</h2>

          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label={t('auth.register.email')}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.register.emailPlaceholder')}
                error={errors.email}
                required
                autoComplete="email"
              />
              
              <Input
                label={t('auth.register.password')}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.register.passwordPlaceholder')}
                error={errors.password}
                required
                autoComplete="new-password"
              />
              
              <Input
                label={t('auth.register.confirmPassword')}
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('auth.register.confirmPasswordPlaceholder')}
                error={errors.confirmPassword}
                required
                autoComplete="new-password"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full mt-6"
              loading={loading}
              disabled={loading}
            >
              {t('auth.register.button')}
            </Button>
          </form>

          {/* Дополнительная информация */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              {t('auth.register.hasAccount')}{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                {t('auth.register.loginLink')}
              </Link>
            </p>
          </div>
        </div>

        {/* Информация о версии */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {t('app.clientPanel')} {t('app.version')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
